/**
 * Loads the Google Maps JavaScript API for the public Voucher Redemption Locator — and ONLY there
 * (the script tag is injected the first time the locator map mounts; no other page pays for it).
 *
 * One bootstrap <script> with `loading=async` and a callback, as Google documents for the Maps JS
 * API (https://developers.google.com/maps/documentation/javascript/load-maps-js-api). No npm loader
 * package: this is the documented "direct script loading" form and adds no dependency.
 *
 * The key is VITE_GOOGLE_MAPS_API_KEY — a BROWSER key, compiled into the public bundle like every
 * Maps JS key. It must be restricted in the Google Cloud console to our HTTP referrers and to the
 * Maps JavaScript API only. It is NOT the server key the `geocode` edge function uses
 * (GOOGLE_MAPS_API_KEY, an edge secret) and must never be the same key.
 *
 * Failure is a normal outcome, never a crash: no key, script blocked / offline, a slow network
 * (timeout), or Google rejecting the key (it calls window.gm_authFailure, possibly AFTER the map has
 * started drawing) all end in "unavailable", and the page shows its list without a map.
 */
/// <reference types="google.maps" />

/** The official @types/google.maps namespace (pinned devDependency), so tsc checks every call. */
export type GoogleMapsApi = typeof google.maps;

/** Pinned release channel: quarterly changes ~4x a year instead of weekly. */
export const GOOGLE_MAPS_CHANNEL = "quarterly";
export const GOOGLE_MAPS_SCRIPT_ID = "vl-google-maps-js";
const CALLBACK = "__vlGoogleMapsReady";
const DEFAULT_TIMEOUT_MS = 15000;

declare global {
  interface Window {
    gm_authFailure?: () => void;
    __vlGoogleMapsReady?: () => void;
  }
}

/**
 * google.maps as it is on the page right now, or undefined. Read through `unknown`: with
 * @types/google.maps loaded, tsc thinks the global always exists, but before the script runs it does not.
 */
function pageMaps(): GoogleMapsApi | undefined {
  return (window as unknown as { google?: { maps?: GoogleMapsApi } }).google
    ?.maps;
}

let loading: Promise<GoogleMapsApi> | null = null;
let authFailed = false;
let hookInstalled = false;
const authListeners = new Set<() => void>();

/** True once Google has rejected the key during this page session. */
export function googleMapsAuthFailed(): boolean {
  return authFailed;
}

/** Subscribe to Google's key-rejection signal; returns the unsubscribe function. */
export function onGoogleMapsAuthFailure(cb: () => void): () => void {
  authListeners.add(cb);
  return () => {
    authListeners.delete(cb);
  };
}

function installAuthFailureHook(): void {
  if (hookInstalled) {
    return; // once per page: a retry must not wrap our own hook again
  }
  hookInstalled = true;
  const previous = window.gm_authFailure;
  window.gm_authFailure = () => {
    authFailed = true;
    authListeners.forEach((cb) => {
      try {
        cb();
      } catch {
        /* a listener must never break the others */
      }
    });
    if (previous) {
      previous();
    }
  };
}

/** Build the bootstrap URL. Exported for the unit test; the key is URL-encoded, never logged. */
export function googleMapsScriptUrl(apiKey: string): string {
  const q = new URLSearchParams({
    key: apiKey,
    v: GOOGLE_MAPS_CHANNEL,
    loading: "async",
    callback: CALLBACK,
  });
  return `https://maps.googleapis.com/maps/api/js?${q.toString()}`;
}

/**
 * Resolve with google.maps (core + "maps" library imported), or reject. Concurrent callers share one
 * load; a FAILED load is forgotten so a later mount can try again. EVERY path — fresh script or reuse
 * of a google.maps that is already on the page — is bounded by the same deadline, so a load can never
 * stay pending (external audit P3, be46519e).
 */
export function loadGoogleMaps(
  apiKey: string | undefined,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<GoogleMapsApi> {
  const key = (apiKey ?? "").trim();
  if (!key) {
    return Promise.reject(new Error("google-maps: no browser key configured"));
  }
  if (authFailed) {
    return Promise.reject(new Error("google-maps: key rejected"));
  }
  if (loading) {
    return loading;
  }

  installAuthFailureHook();
  // `failed` + a pre-declared `attempt` let finish() run even synchronously inside the executor
  // (e.g. importLibrary throwing) without a TDZ error, and keep a dead attempt out of `loading`.
  let attempt: Promise<GoogleMapsApi> | null = null;
  let failed = false;
  attempt = new Promise<GoogleMapsApi>((resolve, reject) => {
    let settled = false;
    const finish = (err: Error | null, maps?: GoogleMapsApi) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      if (err) {
        document.getElementById(GOOGLE_MAPS_SCRIPT_ID)?.remove();
        failed = true;
        if (attempt !== null && loading === attempt) {
          loading = null;
        }
        reject(err);
      } else {
        resolve(maps as GoogleMapsApi);
      }
    };
    const timer = window.setTimeout(
      () => finish(new Error("google-maps: timed out")),
      timeoutMs,
    );
    const importAll = (maps: GoogleMapsApi) => {
      try {
        Promise.all([maps.importLibrary("core"), maps.importLibrary("maps")])
          .then(() => finish(null, maps))
          .catch(() => finish(new Error("google-maps: library import failed")));
      } catch {
        // a synchronous throw must also settle (and forget) this attempt at once
        finish(new Error("google-maps: library import failed"));
      }
    };

    // A retry after a timeout: the first script may have finished after we gave up. Reuse it rather
    // than loading Google a second time ("included multiple times") — still under the deadline.
    const existing = pageMaps();
    if (existing && typeof existing.importLibrary === "function") {
      importAll(existing);
      return;
    }

    window[CALLBACK] = () => {
      const maps = pageMaps();
      if (!maps || typeof maps.importLibrary !== "function") {
        finish(new Error("google-maps: loaded without google.maps"));
        return;
      }
      importAll(maps);
    };
    const s = document.createElement("script");
    s.id = GOOGLE_MAPS_SCRIPT_ID;
    s.src = googleMapsScriptUrl(key);
    s.async = true;
    s.onerror = () => finish(new Error("google-maps: script failed to load"));
    document.head.appendChild(s);
  });
  if (!failed) {
    loading = attempt;
  }
  return attempt;
}

/** Test-only: forget all module state. */
export function __resetGoogleMapsLoaderForTests(): void {
  loading = null;
  authFailed = false;
  hookInstalled = false;
  authListeners.clear();
  document.getElementById(GOOGLE_MAPS_SCRIPT_ID)?.remove();
  delete window[CALLBACK];
  delete window.gm_authFailure;
}
